import React, { useCallback, useEffect, useRef, useState } from "react"
import { Box, Button, Typography } from "@mui/material"
import { useNavigate, useSearchParams } from "react-router-dom"
import { jwtDecode } from "jwt-decode"
import { useDispatch, useSelector } from "react-redux"
import {
  AuthSubtitle,
  AuthTitle,
  ChangeNumberText,
  CodeTitle,
  ContentContainer,
  RepeatCodeTitle,
  ResendText,
  StyledMuiOtp,
} from "./styled"
import { formatPhoneNumber } from "../../utils/formatters"
import { getFingerPrint as getFingerprint } from "../../utils/fingerprint"
import { authRequest } from "../../api/hooks/useAuthRequest"
import { AuthResults } from "../../api/interface"
import { IExchangeJwtPayload, IJwtPayload } from "./hooks/interface"
import {
  QUERY_PARAMS_EXCHANGE_TOKEN_KEY,
  ROUTES_PATHS,
} from "../../utils/constants"
import {
  setCustomerId,
  setPhoneNumber,
  setToken,
} from "../../store/slices/auth"
import { useRegistrationConfirm } from "./hooks/useRegistrationConfirm"
import { useResendRegistrationOtp } from "./hooks/useResendRegistrationOtp"
import { useIdentification } from "./hooks/useIdentification"
import { RootState } from "../../store/store"
import ErrorHandlerModal from "../../components/Modal/ErrorHandlerModal"

type RegistrationOtpConfirmationProps = {
  redirectUrlProp?: string
  flow?: "identification" | "default"
}

const RegistrationOtpConfirmation = ({
  redirectUrlProp,
  flow = "default",
}: RegistrationOtpConfirmationProps) => {
  const [otp, setOtp] = useState("")
  const [timer, setTimer] = useState(60)
  const [otpState, setOtpState] = useState(2)
  const [open, setOpen] = useState(false)
  const identificationToken = useRef<string | null>(null)

  // Получаем номер телефона из Redux
  const registrationPhoneNumber = useSelector(
    (state: RootState) => state.registration.phoneNumber,
  )
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState("")

  const {
    data: confirmResult,
    mutate: confirmMutate,
    status: confirmStatus,
  } = useRegistrationConfirm()

  const {
    data: identificationResult,
    mutate: identificationMutate,
    status: identificationStatus,
  } = useIdentification()

  const {
    data: resendOtpResult,
    mutate: resendOtpMutate,
    status: resendOtpStatus,
  } = useResendRegistrationOtp()
  
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  
  const redirectUrl = searchParams.get("redirectUrl")
  
  useEffect(() => {
    let timerId: NodeJS.Timeout
    if (otpState === 2 && timer > 0) {
      timerId = setInterval(() => setTimer((prev) => prev - 1), 1000)
    } else if (timer === 0) {
      setOtpState(1)
    }
    return () => clearInterval(timerId)
  }, [otpState, timer])

  useEffect(() => {
    if (otp.length === 6) {
      confirmMutate({ code: otp })
    }
  }, [otp, confirmMutate])

  useEffect(() => {
    if (confirmStatus === "error") {
      navigate(`/${ROUTES_PATHS.error}`)
      return
    }

    if (confirmResult?.result === "Error") {
      if (confirmResult.errorDetails?.code === "otp_attempt_failed") {
        setOtp("")
        return
      }

      if (
        confirmResult.errorDetails?.code === "otp_attempts_exceeded" ||
        confirmResult.errorDetails?.code === "otp_flow_failed"
      ) {
        navigate(`/${ROUTES_PATHS.authError}`)
        return
      }

      if (
        confirmResult.errorDetails?.code === "customer_is_blocked" ||
        confirmResult.errorDetails?.code === "customer_is_closed" ||
        confirmResult.errorDetails?.code === "fraud_detected"
      ) {
        setOpen(true)
        return
      }
    }

    if (confirmResult?.result === "Registered") {
      if (confirmResult.token === null) {
        navigate(`/${ROUTES_PATHS.error}`)
        return
      }

      const jwtToken = confirmResult.token
      if (jwtToken) {
        const payload = jwtDecode<IJwtPayload>(jwtToken)
        dispatch(setCustomerId(payload.CustomerId))
        dispatch(setPhoneNumber(payload.PhoneNumber))
        dispatch(setToken(jwtToken))

        if (!payload.AdditionalData?.EncryptedCustomerData) {
          navigate(`/${ROUTES_PATHS.topUpMbankAccountIdentification}`)
          return
        }

        if (flow === "identification") {
          const fingerprint = getFingerprint(true)

          authRequest(jwtToken, { fingerprint: fingerprint!, url: "" })
            .then((authResult) => {
              if (
                authResult.result !== AuthResults.AUTHORIZED ||
                !authResult.token
              ) {
                navigate(`/${ROUTES_PATHS.authError}`)
                return
              }

              identificationToken.current = authResult.token
              dispatch(setToken(authResult.token))
              identificationMutate({ token: authResult.token })
            })
            .catch(() => {
              navigate(`/${ROUTES_PATHS.error}`)
            })
        } else if (redirectUrl || redirectUrlProp) {
          navigate(decodeURIComponent(redirectUrl || redirectUrlProp || ""))
        } else {
          navigate(`/${ROUTES_PATHS.main}`)
        }
      }
    }
  }, [
    confirmResult,
    confirmStatus,
    dispatch,
    navigate,
    redirectUrl,
    redirectUrlProp,
    flow,
    identificationMutate,
  ])

  useEffect(() => {
    if (identificationStatus === "error") {
      navigate(`/${ROUTES_PATHS.error}`)
      return
    }

    if (identificationResult?.identificationRequestId) {
      const token = identificationToken.current || confirmResult?.token
      navigate(
        `/${ROUTES_PATHS.topUpMbankAccountIdentification}/${identificationResult.identificationRequestId}?${QUERY_PARAMS_EXCHANGE_TOKEN_KEY}=${token}`,
      )
    }
  }, [identificationResult, identificationStatus, navigate, confirmResult])

  useEffect(() => {
    if (resendOtpStatus === "error") {
      setOpen(true)
      return
    }

    if (resendOtpResult?.result === "Sent") {
      setOtpState(2)
      setTimer(60)
      return
    }

    if (resendOtpResult?.result === "Wait") {
      setOtpState(2)
      setTimer(10)
      return
    }

    if (
      resendOtpResult?.result === "Error" &&
      resendOtpResult.errorDetails?.code === "otp_attempts_exceeded"
    ) {
      setOpen(true)
      setOtpState(3)
    }
  }, [resendOtpResult, resendOtpStatus, navigate])

  useEffect(() => {
    // Если номер телефона есть в Redux, используем его
    if (registrationPhoneNumber) {
      setCustomerPhoneNumber(registrationPhoneNumber)
    } else {
      // Иначе пытаемся получить из токена
      const jwtToken = searchParams.get(QUERY_PARAMS_EXCHANGE_TOKEN_KEY)
      if (jwtToken) {
        try {
          const payload = jwtDecode<IExchangeJwtPayload>(jwtToken)
          setCustomerPhoneNumber(payload.PhoneNumber)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Ошибка при декодировании токена:", error)
        }
      }
    }
  }, [searchParams, registrationPhoneNumber])

  const handleInputChange = (value: string) => {
    const filteredValue = value.replace(/[^0-9]/g, "")
    setOtp(filteredValue)
  }

  const handleResendOtp = useCallback(() => {
    resendOtpMutate()
  }, [resendOtpMutate])

  const renderBottomSection = () => {
    switch (otpState) {
      case 1:
        return (
          <Button
            variant="text"
            sx={ChangeNumberText}
            onClick={handleResendOtp}
          >
            Отправить код повторно
          </Button>
        )
      case 2:
        return (
          <Typography variant="body1" sx={ResendText}>
            {`Повторно отправить код можно будет\nчерез ${timer} секунд.`}
          </Typography>
        )
      case 3:
        return (
          <Typography variant="h6" sx={RepeatCodeTitle}>
            Лимит по отправке смс с кодом исчерпан
          </Typography>
        )
      default:
        return null
    }
  }

  return (
    <>
      <ErrorHandlerModal open={open} toggleMOdal={() => setOpen(!open)} />
      <Box sx={ContentContainer}>
        <Typography variant="h3" sx={AuthTitle}>
          Код подтверждения
        </Typography>
        <Typography variant="h6" sx={AuthSubtitle}>
          Код подтверждения отправлен на номер{" "}
          {formatPhoneNumber(customerPhoneNumber)}
        </Typography>
        <Typography variant="h4Bold" sx={CodeTitle}>
          Код из СМС
        </Typography>
        <StyledMuiOtp
          autoFocus
          length={6}
          onChange={handleInputChange}
          value={otp}
          error={
            confirmResult?.result === "Error" &&
            confirmResult.errorDetails?.code === "otp_attempt_failed"
          }
          TextFieldsProps={{
            variant: "standard",
            type: "number",
          }}
        />
        {renderBottomSection()}
      </Box>{" "}
    </>
  )
}

RegistrationOtpConfirmation.defaultProps = {
  redirectUrlProp: undefined,
  flow: "default",
}

export default RegistrationOtpConfirmation
